import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronDown, ChevronRight, ServerCog } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, Field, Input, ThemeToggle } from '@aeris/ui'

import logoHorizontal from '../assets/aeris-logo-horizontal.png'
import {
  ShellConfigError,
  writeShellConfig,
  type ShellConfig,
} from '../lib/shell-config.ts'

/**
 * A primeira tela do aplicativo instalado: onde fica o servidor desta loja.
 *
 * ## Por que ela vem ANTES do login
 *
 * Sem servidor configurado, a tela de login aparece, a pessoa digita o e-mail
 * dela e recebe "não foi possível falar com o servidor". A mensagem é
 * verdadeira e inútil: ela descreve o sintoma de uma configuração que ninguém
 * pediu para preencher. Quem instalou não erra a senha — ele ainda não disse
 * qual é o Aeris dele.
 *
 * ## Por que não é uma rota
 *
 * Porque o roteador vive dentro da aplicação e esta pergunta é anterior a ela.
 * Renderizada acima do `AuthProvider`, ela também impede a primeira consulta de
 * sessão de sair para o vazio.
 *
 * Depois de configurada, o mesmo formulário volta a ser alcançável em
 * `/servidor`, porque servidor muda: a loja troca de hospedagem, alguém digitou
 * errado, o técnico apontou para o ambiente de teste.
 */

const address = (rotulo: string) =>
  z
    .string()
    .trim()
    .max(255)
    .refine(
      (value) => value === '' || /^https?:\/\/.+/i.test(value),
      `${rotulo} começa com https:// (ou http:// numa rede local)`
    )

const schema = z.object({
  serverUrl: address('O endereço').refine(
    (value) => value !== '',
    'Informe o endereço do servidor'
  ),
  /**
   * O Aeris Edge desta loja — opcional, e vazio na esmagadora maioria dos
   * casos (F042/F155).
   *
   * Vazio é uma resposta legítima: a maior parte das lojas não tem Edge. Por
   * isso ele não entra na validação de "informe o endereço", e por isso mora
   * atrás de um "mostrar mais" — a primeira tela do produto tem UMA pergunta.
   */
  edgeUrl: address('O endereço do Edge'),
})

type Form = z.infer<typeof schema>

/**
 * Confere se há um Aeris de verdade do outro lado antes de dar a tela por
 * pronta.
 *
 * Gravar e seguir sem conferir devolveria a pessoa ao login com um endereço
 * errado — o mesmo beco de onde ela acabou de sair. `/health` responde sem
 * autenticação e é a menor pergunta possível que só um Aeris sabe responder.
 */
const reachable = async (): Promise<boolean> => {
  try {
    const response = await fetch('/health', {
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return false

    const body: unknown = await response.json()
    return (
      typeof body === 'object' &&
      body !== null &&
      'service' in body &&
      body.service === 'aeris-api'
    )
  } catch {
    return false
  }
}

export const ServerSetup = ({
  config,
  onConfigured,
}: {
  config: ShellConfig
  onConfigured: (next: ShellConfig) => void
}): ReactNode => {
  const [formError, setFormError] = useState<string | null>(null)
  /*
   * O Edge começa escondido, e aberto quando já existe um.
   *
   * Escondido porque a primeira tela do produto faz UMA pergunta, e a loja que
   * tem Edge é a exceção. Aberto quando já há um endereço gravado porque, aí,
   * escondê-lo seria esconder da pessoa a configuração que ela veio conferir.
   */
  const [showEdge, setShowEdge] = useState(config.edgeUrl !== '')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { serverUrl: config.serverUrl, edgeUrl: config.edgeUrl },
  })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)

    try {
      const saved = await writeShellConfig({
        serverUrl: values.serverUrl,
        edgeUrl: values.edgeUrl,
      })

      /*
       * O shell troca o destino em memória assim que responde ao PUT, então
       * esta chamada já sai pelo endereço novo — sem reiniciar o programa.
       */
      if (!(await reachable())) {
        setFormError(
          'Gravei o endereço, mas não encontrei um Aeris nele. Confira se está certo e se esta máquina tem internet.'
        )
        return
      }

      onConfigured(saved)
    } catch (error) {
      setFormError(
        error instanceof ShellConfigError
          ? error.message
          : 'Não foi possível gravar a configuração nesta máquina.'
      )
    }
  })

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center bg-(--aeris-navy-900) px-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <main className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img
            src={logoHorizontal}
            alt="Aeris One — Sistema de Gestão"
            className="h-11 w-auto brightness-0 invert"
          />
        </div>

        <form
          onSubmit={(event) => {
            void onSubmit(event)
          }}
          noValidate
          className="flex flex-col gap-4 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-6 shadow-(--shadow-lg)"
        >
          <div className="flex flex-col gap-1">
            <h1 className="flex items-center gap-2 text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
              <ServerCog aria-hidden="true" className="h-5 w-5" />
              Qual é o seu Aeris?
            </h1>
            <p className="text-(length:--text-xs) text-(--text-muted)">
              O endereço do servidor da sua empresa. Você só precisa responder
              isto uma vez nesta máquina.
            </p>
          </div>

          {formError === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {formError}
            </p>
          )}

          <Field
            label="Endereço do servidor"
            required
            error={errors.serverUrl?.message}
            hint="Quem instalou o Aeris para a sua empresa passou este endereço."
          >
            {(field) => (
              <Input
                {...field}
                {...register('serverUrl')}
                type="url"
                autoFocus
                spellCheck={false}
                autoCapitalize="none"
                placeholder="https://api.aerisone.com.br"
              />
            )}
          </Field>

          {/*
            O Edge é o que faz a loja continuar vendendo com a internet fora
            (F042). O proxy do desktop, o cliente e o contrato existem desde
            então; o que faltava era este campo — sem ele, ligar o Edge exigia
            editar `settings.json` à mão na máquina.
          */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                setShowEdge((open) => !open)
              }}
              className="flex items-center gap-1 self-start text-(length:--text-xs) text-(--text-muted) hover:text-(--text-primary)"
            >
              {showEdge ? (
                <ChevronDown aria-hidden="true" className="h-3 w-3" />
              ) : (
                <ChevronRight aria-hidden="true" className="h-3 w-3" />
              )}
              Esta loja tem um Aeris Edge?
            </button>

            {showEdge ? (
              <Field
                label="Endereço do Edge"
                error={errors.edgeUrl?.message}
                hint={
                  /*
                   * O pareamento (F197): trocar este endereço desfaz o
                   * pareamento, e o caixa pareia de novo no PDV — com a
                   * internet no ar.
                   */
                  config.edgeUrl === ''
                    ? 'O servidor local da loja, que mantém o caixa funcionando quando a internet cai. Deixe em branco se não houver.'
                    : config.edgePaired === true
                      ? 'Este caixa está pareado com o Edge. Trocar o endereço desfaz o pareamento.'
                      : 'Falta parear este caixa: abra o PDV com a internet no ar e toque em "Parear com o Edge da loja".'
                }
              >
                {(field) => (
                  <Input
                    {...field}
                    {...register('edgeUrl')}
                    type="url"
                    spellCheck={false}
                    autoCapitalize="none"
                    placeholder="http://192.168.0.10:8090"
                  />
                )}
              </Field>
            ) : null}
          </div>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Conferindo…' : 'Continuar'}
          </Button>
        </form>
      </main>
    </div>
  )
}
